import type { MaybeRefOrGetter } from 'vue';
import type { FieldSelection, GlobalCollectionId, GlobalFieldId, ResolvedMonospaceCollection } from '#layers/data-model/public';
import { computed, toValue } from 'vue';
import { usePermissionsStore } from '#layers/access/stores/permissions';
import { filterByPermissions } from '#layers/access/utils/permissions/filter-field-selection';
import { expandRequiredFields, getCollectionIdentityFields, getRelationPrimitiveFields, useWorkspaceSchemaStore } from '#layers/data-model/public';

export interface UseProcessedFieldSelectionOptions {
	collection: MaybeRefOrGetter<ResolvedMonospaceCollection | undefined>;
	selection: MaybeRefOrGetter<FieldSelection | undefined>;
}

function resolveCollectionField(resolveCollection: (id: GlobalCollectionId) => ResolvedMonospaceCollection | undefined, collectionId: GlobalCollectionId, apiName: string) {
	const targetCol = resolveCollection(collectionId);
	if (!targetCol) return;

	const primitive = targetCol.primitiveFields?.find((f) => f.apiName === apiName);

	if (primitive) {
		return {
			id: primitive.id as GlobalFieldId,
			isRelation: false as const,
			isStudioOnly: !!primitive.isStudioOnly,
		};
	}

	const relation = targetCol.relationFields?.find((f) => f.apiName === apiName);

	if (relation) {
		return {
			id: relation.id as GlobalFieldId,
			isRelation: true as const,
			oppositeCollectionId: relation.oppositeCollectionId as GlobalCollectionId,
			isStudioOnly: !!relation.isStudioOnly,
		};
	}
}

export function useProcessedFieldSelection(options: UseProcessedFieldSelectionOptions) {
	const { findCollectionById } = useWorkspaceSchemaStore();
	const permissionsStore = usePermissionsStore();

	const processed = computed(() => {
		const col = toValue(options.collection);
		const rawSelection = toValue(options.selection);
		if (!col || !rawSelection) return { selection: undefined, excluded: [] as string[] };

		const resolveCollection = (id: GlobalCollectionId) =>
			id === col.id ? col : findCollectionById(id);

		const resolveField = (collectionId: GlobalCollectionId, apiName: string) =>
			resolveCollectionField(resolveCollection, collectionId, apiName);

		const { filtered, excluded } = filterByPermissions({
			collectionId: col.id,
			selection: rawSelection,
			canReadField: (collectionId, fieldId) => permissionsStore.canReadField(collectionId, fieldId),
			canRead: (collectionId) => permissionsStore.canRead(collectionId),
			isAdmin: permissionsStore.isAdmin,
			resolveField,
		});

		const selection = expandRequiredFields({
			collectionId: col.id,
			selection: filtered,
			resolveField,
			getCollectionKeyFieldNames: (collectionId) => {
				const targetCol = resolveCollection(collectionId);
				if (!targetCol) return [];
				// Identity, not primary key: a keyless junction is cached under its foreign keys, so
				// omitting them here caches the row under an incomplete key that aliases into every
				// sibling row. This has to hold whatever the display template or permission filtering
				// leaves in the selection.
				return getCollectionIdentityFields(targetCol).map((f) => f.apiName);
			},
			getRelationJoinFields: (collectionId, relationApiName) => {
				const targetCol = resolveCollection(collectionId);
				const relation = targetCol?.relationFields?.find((f) => f.apiName === relationApiName);
				if (!relation) return { ownFields: [], oppositeFields: [] };
				const { fields, oppositeFields } = getRelationPrimitiveFields(relation);
				return {
					ownFields: fields.map((f) => f.apiName),
					oppositeFields: oppositeFields.map((f) => f.apiName),
				};
			},
		});

		return { selection, excluded };
	});

	return {
		selection: computed(() => processed.value.selection),
		excluded: computed(() => processed.value.excluded),
	};
}
